import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Columns3 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';

// Picks which columns the v2 CSV export contains and in what order; saved per event.
const ExportColumnsDialog = ({ eventId, triggerClassName = '', size = 'default' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['exportColumns', eventId],
    queryFn: () => ApiService.getExportColumns(eventId),
    enabled: isOpen && !!eventId,
  });

  useEffect(() => {
    if (!data) return;
    const selectedKeys = new Set((data.selected || []).map((c) => c.key));
    const selected = (data.selected || []).map((c) => ({ ...c, enabled: true }));
    const remaining = (data.available || [])
      .filter((c) => !selectedKeys.has(c.key))
      .map((c) => ({ ...c, enabled: false }));
    setColumns([...selected, ...remaining]);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload) => ApiService.saveExportColumns(eventId, payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(['exportColumns', eventId], saved);
      showSuccess('Export columns saved');
      setIsOpen(false);
    },
    onError: (error) => showError(error.message || 'Failed to save export columns'),
  });

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[index], next[target]] = [next[target], next[index]];
    setColumns(next);
  };

  const toggle = (index) =>
    setColumns(columns.map((c, i) => (i === index ? { ...c, enabled: !c.enabled } : c)));

  const rename = (index, label) =>
    setColumns(columns.map((c, i) => (i === index ? { ...c, label } : c)));

  const enabledCount = columns.filter((c) => c.enabled).length;

  return (
    <>
      <Button variant="outline" size={size} className={triggerClassName} onClick={() => setIsOpen(true)}>
        <Columns3 className="w-4 h-4" />
        <span className="text-xs sm:text-sm">Export Columns</span>
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Export columns</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600">
              Tick the columns to export and order them with the arrows. You can also rename the header.
            </p>
            {isLoading ? (
              <p className="text-sm text-gray-500 py-6">Loading…</p>
            ) : (
              <ul className="max-h-[55vh] overflow-y-auto divide-y border rounded-lg">
                {columns.map((column, index) => (
                  <li key={column.key} className="flex items-center gap-2 px-3 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={column.enabled}
                      onChange={() => toggle(index)}
                      aria-label={`Include ${column.label}`}
                    />
                    <input
                      className="flex-1 min-w-0 rounded border border-transparent px-2 py-1 text-sm hover:border-gray-200 focus:border-gray-300"
                      value={column.label || ''}
                      onChange={(e) => rename(index, e.target.value)}
                      disabled={!column.enabled}
                    />
                    <span className="hidden sm:block text-xs text-gray-400 truncate max-w-[10rem]">{column.key}</span>
                    <Button variant="ghost" size="sm" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => move(index, 1)} disabled={index === columns.length - 1} aria-label="Move down">
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => saveMutation.mutate([])}
                disabled={saveMutation.isPending}
              >
                Reset to all columns
              </Button>
              <Button
                onClick={() => saveMutation.mutate(columns.filter((c) => c.enabled).map(({ key, label }) => ({ key, label })))}
                disabled={saveMutation.isPending || enabledCount === 0}
              >
                Save ({enabledCount})
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportColumnsDialog;
